import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { Router } from '@angular/router';

interface RequestOptions {
  params?: { [key: string]: any };
  body?: { [key: string]: any };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  tokenExpiresInSecond: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(private http: HttpClient, private router: Router) { }

  private createHeaders(): HttpHeaders {
    let authorizationToken: string = localStorage.getItem('AccessToken') || '';
    let headers: { [key: string]: string } = {
      'x-access-token': authorizationToken
    };

    let httpHeaders = new HttpHeaders();
    if (headers) {
      for (const key in headers) {
        if (headers.hasOwnProperty(key)) {
          httpHeaders = httpHeaders.set(key, headers[key]);
        }
      }
    }
    return httpHeaders;
  }

  private createParams(params?: { [key: string]: any }): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          httpParams = httpParams.set(key, params[key]);
        }
      }
    }
    return httpParams;
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage: string = '';
    console.log(error);

    // if (error.status === 401) {
    //   // Redirect to logout if the error status is 401
    //   this.router.navigate(['/logout']);
    //   return throwError('Unauthorized access, redirecting to logout.');
    // }

    if (error.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${error.error.message}`; // use error.message to get the error message
    } else if (error.error.data == undefined || error.error.data.length == 0) {
      errorMessage = error.message;
    }


    else {
      errorMessage = error.message; // use error.status and error.message to get the error details
    }
    return throwError(errorMessage);
  }

  replacePathInfo(route: string, pathInfo?: { [key: string]: any }): string {
    if (pathInfo) {
      const replaceableKeys = Object.keys(pathInfo);
      for (const key of replaceableKeys) {
        const str = '{' + key + '}';
        route = route.replace(str, pathInfo[key]);
      }
    }
    return route;
  }

  request<T>(
    method: string,
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication?: boolean
  ): Observable<T> {
    let httpOptions: any = {
      body: options?.body,
      params: this.createParams(options?.params),
    };

    if (requiresAuthentication) {
      httpOptions['headers'] = this.createHeaders();
    }

    const req = new HttpRequest<T>(method, endpoint, httpOptions.body, {
      headers: httpOptions.headers,
      params: httpOptions.params,
    });

    return this.http.request<T>(req).pipe(
      filter(
        (event): event is HttpResponse<T> => event instanceof HttpResponse
      ),
      map((response: HttpResponse<T>) => response.body as T),
      catchError(this.handleError)
    );
  }

  get<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>('GET', endpoint, options, requiresAuthentication);
  }

  post<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>('POST', endpoint, options, requiresAuthentication);
  }
  put<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>('PUT', endpoint, options, requiresAuthentication);
  }
  delete<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>('DELETE', endpoint, options, requiresAuthentication);
  }
  patch<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>('PATCH', endpoint, options, requiresAuthentication);
  }
}
